import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import CommonModal from 'jbc-front/components/CommonModal';
import Button from 'jbc-front/components/Button';
import { EMPLOYEE_PAY_LIST } from 'src/constants/EndpointUrls';

import styles from './UnconfirmedPayrollModal.scss';

const UnconfirmedPayrollModal = ({ isOpen, periodType, year, periodMonth, periodQuarter, unconfirmedPaymentMonths, onCancel, onConfirm }) => {
  const { t, i18n } = useTranslation();

  const buildMonthName = dateValue => {
    if (!dateValue) return '';
    moment.locale(i18n.language);
    return moment(dateValue).format('MMMM YYYY');
  };

  const buildMonthYearName = (month, itemYear) => {
    if (!month || !itemYear) return '';
    moment.locale(i18n.language);
    return moment({ month: month - 1, year: itemYear }).format('MMMM YYYY');
  };

  const getItemKey = (item, index) => {
    if (item.paymentDate) return `${item.payrollRulesGroupId}-${item.paymentDate}`;
    return `missing-${item.month}-${item.year}-${index}`;
  };

  const buildPayrollLink = item => {
    const date = moment(item.paymentDate).format('YYYY_MM_DD');
    return `${EMPLOYEE_PAY_LIST}?payroll_rules_groups=${item.payrollRulesGroupId}&year_month=${date}`;
  };

  const renderItemText = item => {
    if (item.status === 'missing') {
      return t('features.ReportIncomeTax.create.unconfirmed_modal.missing_item', {
        monthYear: buildMonthYearName(item.month, item.year)
      });
    }

    const label = t('features.ReportIncomeTax.create.unconfirmed_modal.unconfirmed_item', {
      paymentDate: moment(item.paymentDate).format('DD/MM/YYYY'),
      groupName: item.payrollRulesGroupName || ''
    });

    return (
      <a href={buildPayrollLink(item)} target="_blank" rel="noopener noreferrer" className={styles.payrollLink}>
        {label}
      </a>
    );
  };

  const buildPeriodLabel = () => {
    if (periodType === 'monthly' && periodMonth && year) {
      moment.locale(i18n.language);
      const monthName = moment(periodMonth, 'M').format('MMMM');
      return `${monthName} ${year}`;
    }
    if (periodType === 'quarterly' && periodQuarter && year) {
      return t('features.ReportIncomeTax.create.unconfirmed_modal.quarter_label', { quarter: periodQuarter, year });
    }
    return '';
  };

  const buildPeriodTitle = () => {
    if (periodType === 'monthly' && periodMonth && year) {
      moment.locale(i18n.language);
      const monthName = moment(periodMonth, 'M').format('MMMM');
      return t('features.ReportIncomeTax.create.unconfirmed_modal.period_month_title', { month: monthName, year });
    }
    if (periodType === 'quarterly' && periodQuarter && year) {
      return t('features.ReportIncomeTax.create.unconfirmed_modal.period_quarter_title', { quarter: periodQuarter, year });
    }
    return '';
  };

  const getAffectedMonthsList = () => {
    if (!unconfirmedPaymentMonths.length) return '';

    const monthsSet = new Set();
    unconfirmedPaymentMonths.forEach(item => {
      const monthYear = item.paymentDate ? buildMonthName(item.paymentDate) : buildMonthYearName(item.month, item.year);
      if (monthYear) monthsSet.add(monthYear);
    });

    return Array.from(monthsSet).join(', ');
  };

  const periodLabel = buildPeriodLabel();
  const periodTitle = buildPeriodTitle();
  const affectedMonths = getAffectedMonthsList();

  return (
    <CommonModal
      header={t('features.ReportIncomeTax.create.unconfirmed_modal.title')}
      isOpen={isOpen}
      hideModal={onCancel}
      ariaHideApp={false}
      className={styles.unconfirmedPayrollModal}
    >
      <CommonModal.Header hideModal={onCancel}>{t('features.ReportIncomeTax.create.unconfirmed_modal.title')}</CommonModal.Header>
      <CommonModal.Body>
        <div className={styles.body}>
          <div className={styles.periodTitle}>{periodTitle}</div>
          <div className={styles.declarationTitle}>{t('features.ReportIncomeTax.create.unconfirmed_modal.declaration_title')}</div>
          <p className={styles.description}>
            {t('features.ReportIncomeTax.create.unconfirmed_modal.description_prefix')}
            <strong>{t('features.ReportIncomeTax.create.unconfirmed_modal.description_bold', { periodLabel })}</strong>
            {t('features.ReportIncomeTax.create.unconfirmed_modal.description_suffix')}
          </p>
          <ul className={styles.unconfirmedList}>
            {unconfirmedPaymentMonths.map((item, index) => (
              <li key={getItemKey(item, index)} className={styles.unconfirmedItem}>
                {renderItemText(item)}
              </li>
            ))}
          </ul>
          <p className={styles.warning}>
            {t('features.ReportIncomeTax.create.unconfirmed_modal.warning_prefix', {
              months: affectedMonths
            })}
            <span className={styles.warningHighlight}>{t('features.ReportIncomeTax.create.unconfirmed_modal.warning_highlight_1')}</span>
            <span>{t('features.ReportIncomeTax.create.unconfirmed_modal.warning_highlight_or')}</span>
            <span className={styles.warningHighlight}>{t('features.ReportIncomeTax.create.unconfirmed_modal.warning_highlight_2')}</span>
          </p>
          <p className={styles.question}>{t('features.ReportIncomeTax.create.unconfirmed_modal.question', { periodLabel })}</p>
          <p className={styles.note}>※{t('features.ReportIncomeTax.create.unconfirmed_modal.note')}</p>
        </div>
      </CommonModal.Body>
      <CommonModal.Footer>
        <CommonModal.Buttons>
          <Button onClick={onCancel} className="u-mr20">
            {t('features.ReportIncomeTax.create.unconfirmed_modal.cancel')}
          </Button>
          <Button onClick={onConfirm} primary>
            {t('features.ReportIncomeTax.create.unconfirmed_modal.confirm')}
          </Button>
        </CommonModal.Buttons>
      </CommonModal.Footer>
    </CommonModal>
  );
};

UnconfirmedPayrollModal.propTypes = {
  isOpen: PropTypes.bool,
  periodType: PropTypes.string,
  year: PropTypes.number,
  periodMonth: PropTypes.number,
  periodQuarter: PropTypes.number,
  unconfirmedPaymentMonths: PropTypes.arrayOf(
    PropTypes.shape({
      paymentDate: PropTypes.string,
      month: PropTypes.number,
      year: PropTypes.number,
      status: PropTypes.oneOf(['unconfirmed', 'missing']),
      payrollRulesGroupId: PropTypes.number,
      payrollRulesGroupName: PropTypes.string
    })
  ),
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired
};

UnconfirmedPayrollModal.defaultProps = {
  isOpen: false,
  periodType: 'monthly',
  year: null,
  periodMonth: null,
  periodQuarter: null,
  unconfirmedPaymentMonths: []
};

export default UnconfirmedPayrollModal;
